import { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { AppSettings } from '../../settings/schema';

export type ThemeMode = 'dark' | 'light';

export interface ThemeState {
  mode: ThemeMode;
  primaryColor: string;
  isDark: boolean;
}

export interface ThemeContextValue {
  theme: ThemeState;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}

export interface ThemeProviderProps {
  children: ReactNode;
  themeConfig?: AppSettings['theme'];
}

export function ThemeProvider({ children, themeConfig }: ThemeProviderProps): JSX.Element {
  const mode = themeConfig?.mode ?? 'dark';
  const primaryColor = themeConfig?.primaryColor ?? '#3b82f6';

  const theme = useMemo<ThemeState>(
    () => ({
      mode,
      primaryColor,
      isDark: mode === 'dark',
    }),
    [mode, primaryColor],
  );

  // Inject CSS variables for theming
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--rsc-primary', primaryColor);
    root.style.setProperty('--rsc-primary-rgb', hexToRgb(primaryColor));

    if (mode === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
  }, [mode, primaryColor]);

  const value = useMemo<ThemeContextValue>(() => ({ theme }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '59, 130, 246';
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `${r}, ${g}, ${b}`;
}
